import { useId } from "react";
import type { JSX, ReactNode } from "react";
import { cx } from "./classNames";
import { useFormError } from "./formErrors";
import styles from "./ui.module.css";

/** Properties of {@link Checkbox}. */
export interface CheckboxProps {
  /** Visible text next to the box; it may contain inline content such as a link to a document. */
  readonly label: ReactNode;
  /** Whether the box is checked. */
  readonly checked: boolean;
  /**
   * Called with the new state when the person toggles the box.
   *
   * @param checked - Whether the box is checked now.
   */
  readonly onChange: (checked: boolean) => void;
  /**
   * Accessible name when the visible label alone is not enough, for example in a table row
   * ("Gearbeitete Stunden übertragen" for the visible "Gearbeitete Stunden"). It must contain the
   * visible label (WCAG 2.5.3 Label in Name).
   */
  readonly accessibleName?: string | undefined;
  /** Whether the box cannot be changed. */
  readonly disabled?: boolean | undefined;
  /** Whether the box must be checked before the form can be submitted. */
  readonly required?: boolean | undefined;
  /** Name of the form field. */
  readonly name?: string | undefined;
  /** Identifier of the input, for example for `aria-describedby` of other elements. */
  readonly id?: string | undefined;
  /**
   * Identifiers of the hint or error that describe the box (WCAG 1.3.1, 3.3.1), as `Field` passes
   * them to its control.
   */
  readonly "aria-describedby"?: string | undefined;
  /** Whether the box is in error, for example a required confirmation that is not checked yet. */
  readonly "aria-invalid"?: boolean | undefined;
  /** Additional class name of the label, for example to place it in a grid. */
  readonly className?: string | undefined;
}

/**
 * Checkbox with its visible label (UX-104, UX-121). The label wraps the input, so a tap anywhere on
 * the box or the text toggles it; box and text sit in two grid columns, and the target is at least
 * 44 px high (11.2, WCAG 2.5.8). Inside a `ValidatedForm`, a required box that is not checked
 * shows the form's error below it, tied with `aria-describedby` (F-60).
 *
 * @param props - Component properties.
 * @returns The label with the checkbox, and the error of the form if there is one.
 */
export function Checkbox(props: CheckboxProps): JSX.Element {
  const generatedId = useId();
  const id = props.id ?? generatedId;
  const errorId = `${id}-error`;
  const formError = useFormError(id);
  const describedBy = [props["aria-describedby"], formError === null ? undefined : errorId]
    .filter((value) => value !== undefined && value !== "")
    .join(" ");
  const invalid = props["aria-invalid"] === true || formError !== null;
  const checkbox = (
    <>
      <label className={cx(styles["checkbox"], props.className)}>
        <input
          type="checkbox"
          id={id}
          name={props.name}
          checked={props.checked}
          disabled={props.disabled}
          required={props.required}
          aria-label={props.accessibleName}
          aria-describedby={describedBy === "" ? undefined : describedBy}
          aria-invalid={invalid ? true : undefined}
          onChange={(event) => {
            props.onChange(event.target.checked);
          }}
        />
        <span>{props.label}</span>
      </label>
      {formError === null ? null : (
        <p id={errorId} className={styles["fieldError"]}>
          {formError}
        </p>
      )}
    </>
  );

  return checkbox;
}
